// S&R quote PDF (G-70): the app draws it from the quote JSON (bookings/diagnosis/summary/quote/pdf, or summary/quote) in
// the app theme; the same file is downloaded and, for "Email", posted to summary/quote/pdf/send.
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment";

const PURPLE = [132, 121, 225]; // #8479E1
const NAVY = [14, 47, 126]; // #0E2F7E
const TEXT = [51, 51, 51];
const GREY = [153, 153, 153];
const LIGHT = [243, 244, 247]; // #F3F4F7

const plain = (html) => String(html ?? "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").trim();
const money = (value) => {
  const n = Number(value || 0);
  const text = Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `$(${text})` : `$${text}`;
};
const date = (epoch) => (Number(epoch) > 0 ? moment.unix(epoch).format("MM/DD/YYYY hh:mm A") : "");

// quote = { quote_number, is_approved, submitted_at, approved_at, items[{ heading, data[] }], price_summary | sub_total … }
export function buildQuotePdf(quote, { bookingId, title } = {}) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth();
  const M = 40;

  // header band
  doc.setFillColor(...PURPLE);
  doc.rect(0, 0, W, 70, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold").setFontSize(20);
  doc.text(title || `Quote ${quote.quote_number || ""}`.trim(), M, 42);
  doc.setFont("helvetica", "normal").setFontSize(11);
  doc.text(quote.is_approved ? "Approved" : "Awaiting Approval", W - M, 42, { align: "right" });

  // meta
  let y = 95;
  doc.setTextColor(...TEXT).setFontSize(10);
  [
    bookingId && `Booking # ${bookingId}`,
    date(quote.submitted_at) && `Submitted: ${date(quote.submitted_at)}`,
    quote.is_approved && date(quote.approved_at) && `Approved: ${date(quote.approved_at)}`,
  ].filter(Boolean).forEach((line) => { doc.text(line, M, y); y += 15; });
  y += 5;

  // one table per section; parts (when the API sends them) as grey rows under their item
  (quote.items || []).forEach((section) => {
    const body = [];
    (section.data || []).forEach((row) => {
      const off = Number(row.is_selected) === 0;
      body.push([{ content: plain(row.option) + (off ? "  (Not approved)" : ""), styles: off ? { textColor: GREY } : {} },
        { content: row.total_cost_text ?? money(row.total_cost), styles: { halign: "right", ...(off ? { textColor: GREY } : {}) } }]);
      (row.parts || []).forEach((p) => body.push([
        { content: `    ${plain(p.title)}${p.quantity ? ` x ${p.quantity}` : ""}`, styles: { textColor: GREY, fontSize: 9 } },
        { content: p.price_text ?? "", styles: { halign: "right", textColor: GREY, fontSize: 9 } },
      ]));
    });
    autoTable(doc, {
      startY: y,
      margin: { left: M, right: M },
      head: [[plain(section.heading), { content: "Total", styles: { halign: "right" } }]],
      body,
      theme: "plain",
      headStyles: { fillColor: LIGHT, textColor: [91, 97, 107], fontStyle: "bold", fontSize: 10 },
      bodyStyles: { textColor: TEXT, fontSize: 10, lineColor: [238, 240, 244], lineWidth: { bottom: 0.5 } },
      columnStyles: { 1: { cellWidth: 110 } },
    });
    y = doc.lastAutoTable.finalY + 14;
  });

  // price summary (right half), then the net total bar
  const ps = quote.price_summary;
  const rows = ps
    ? [
      ["Sub Total", money(ps.sub_total)],
      ["Service Discount", money(ps.service_discount)],
      ["Recommended Items Discount", money(ps.recommended_items_discount)],
      ["Total Discount", money(ps.total_discount)],
      ["Amount After Discount", money(ps.amount_after_discount)],
      ...(Number(ps.claim_amount) ? [["Claim Amount", money(ps.claim_amount)]] : []),
      ["Taxes", money(ps.taxes)],
      ["Platform Fee", money(ps.platform_fee)],
    ]
    : [["Sub Total", money(quote.sub_total)], ["Total Discount", String(quote.total_discount ?? "")]];
  autoTable(doc, {
    startY: y,
    margin: { left: W / 2, right: M },
    body: rows,
    theme: "plain",
    bodyStyles: { textColor: TEXT, fontSize: 10, lineColor: [238, 240, 244], lineWidth: { bottom: 0.5 } },
    columnStyles: { 1: { halign: "right" } },
  });
  y = doc.lastAutoTable.finalY + 8;
  if (y > doc.internal.pageSize.getHeight() - 80) { doc.addPage(); y = 50; }
  doc.setFillColor(...LIGHT);
  doc.roundedRect(W / 2, y, W / 2 - M, 32, 6, 6, "F");
  doc.setFont("helvetica", "bold").setFontSize(12).setTextColor(...TEXT);
  doc.text("Net Total", W / 2 + 12, y + 21);
  doc.setTextColor(...NAVY);
  doc.text(ps ? money(ps.net_total) : (quote.total_text ?? money(quote.total)), W - M - 12, y + 21, { align: "right" });

  return doc;
}
